import { RotateCcw, Send, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { HitlBanner } from "@/features/threads/components/HitlBanner";
import { MessageBubble } from "@/features/threads/components/MessageBubble";
import { ToolCallBlock } from "@/features/threads/components/ToolCallBlock";
import { ToolResultBlock } from "@/features/threads/components/ToolResultBlock";

import {
  type DashboardContext,
  type ObsBlock,
  useObservabilityAgent,
} from "../hooks/useObservabilityAgent";
import { ReportDownloadBlock } from "./ReportDownloadBlock";

interface Props {
  dashboard: DashboardContext;
}

/**
 * Painel lateral direito (recolhivel) do agente de observabilidade. Vive na
 * tela admin de Observabilidade. Tool-calling com HITL (cada tool exige
 * aprovacao) + relatorios PDF para download.
 */
export function ObservabilityAgentPanel({ dashboard }: Props) {
  const { t } = useTranslation("observabilityAgent");
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const { blocks, streaming, canSend, send, decide, reset } =
    useObservabilityAgent();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [blocks]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={t("title")}
        aria-label={t("title")}
        className="border-line bg-card text-foreground hover:bg-accent hover:border-brand-400 sticky top-4 flex h-[calc(100vh-7rem)] shrink-0 cursor-pointer flex-col items-center justify-center gap-4 self-start rounded-xl border px-4 py-8 text-sm font-semibold shadow-sm transition-colors"
      >
        <Sparkles className="text-brand-500 h-7 w-7" />
        <span className="rotate-180 whitespace-nowrap [writing-mode:vertical-rl]">
          {t("title")}
        </span>
      </button>
    );
  }

  function doSend() {
    const v = text.trim();
    if (!v || !canSend) return;
    send(v, dashboard);
    setText("");
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      doSend();
    }
  }

  function renderBlock(b: ObsBlock) {
    switch (b.kind) {
      case "bubble":
        return (
          <MessageBubble
            key={b.key}
            role={b.role}
            content={b.content}
            streaming={b.streaming}
          />
        );
      case "tool_call":
        return (
          <ToolCallBlock key={b.key} toolName={b.toolName} args={b.args} />
        );
      case "tool_result":
        return <ToolResultBlock key={b.key} outputPreview={b.outputPreview} />;
      case "hitl":
        return (
          <HitlBanner
            key={b.key}
            hitlRequestId={b.hitlRequestId}
            toolName={b.toolName}
            toolInput={b.toolInput}
            resolved={b.resolved}
            decision={b.decision}
            decisionComment={b.comment}
            onDecide={(payload) => decide(b.hitlRequestId, payload)}
          />
        );
      case "artifact":
        return (
          <ReportDownloadBlock
            key={b.key}
            filename={b.filename}
            mimeType={b.mimeType}
            contentBase64={b.contentBase64}
            sizeBytes={b.sizeBytes}
          />
        );
      default:
        return null;
    }
  }

  return (
    <div className="border-line bg-card sticky top-4 flex h-[calc(100vh-7rem)] w-[380px] shrink-0 flex-col rounded-xl border shadow-sm">
      <header className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="text-brand-500 h-4 w-4" />
          {t("title")}
        </span>
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={reset}
            title={t("newChat")}
            aria-label={t("newChat")}
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setOpen(false)}
            title={t("collapse")}
            aria-label={t("collapse")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto p-3">
        {blocks.length === 0 ? (
          <p className="text-muted-foreground px-2 py-8 text-center text-sm">
            {t("greeting")}
          </p>
        ) : (
          blocks.map(renderBlock)
        )}
      </div>

      <div className="border-t p-3">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("placeholder")}
          rows={2}
          disabled={streaming}
        />
        <div className="mt-2 flex justify-end">
          <Button onClick={doSend} disabled={!canSend || text.trim() === ""}>
            <Send className="h-4 w-4" />
            {t("send")}
          </Button>
        </div>
      </div>
    </div>
  );
}
